/** Meldungs-Detail (F19): Felder, Screenshots, Verlauf + Aktionen (F21/F22).
 * Styling nach Design-Handoff (Screen 04). */

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { toast } from "sonner"
import { ScreenshotBild } from "@/components/erfassung/ScreenshotBild"
import { MeldungsChat } from "@/components/meldung/MeldungsChat"
import { PraezisierungsAssistent } from "@/components/review/PraezisierungsAssistent"
import { ReviewAktionen } from "@/components/review/ReviewAktionen"
import { PRIO_LABEL, STATUS_FARBE, STATUS_LABEL, TYP_LABEL } from "@/lib/labels"
import { api, apiError } from "@/services/api"
import { useAuthStore } from "@/store/auth"
import type { Prioritaet } from "@/types"

/** Sektions-Karte + Uppercase-Überschrift (Design Screen 04) */
export const SEKTION = "rounded-xl border bg-card p-5 shadow-card"
export const SEKTION_TITEL =
  "mb-2.5 text-[13px] font-bold uppercase tracking-wide text-foreground/70"

export function MeldungDetailPage() {
  const { id } = useParams<{ id: string }>()
  const reportId = Number(id)
  const queryClient = useQueryClient()
  const istKundenadmin = useAuthStore((s) => s.isKundenadmin())
  const istReviewer = useAuthStore((s) => s.isReviewer())
  const [verwerfenOffen, setVerwerfenOffen] = useState(false)
  const [begruendung, setBegruendung] = useState("")

  const { data: report, error } = useQuery({
    queryKey: ["reports", "detail", reportId],
    queryFn: () => api.reports.get(reportId),
    enabled: Number.isFinite(reportId),
  })

  const invalidieren = () => {
    void queryClient.invalidateQueries({ queryKey: ["reports"] })
  }

  const prioAendern = useMutation({
    mutationFn: (prioritaet: Prioritaet) => api.reports.update(reportId, { prioritaet }),
    onSuccess: () => {
      invalidieren()
      toast.success("Priorität geändert")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const verwerfen = useMutation({
    mutationFn: () => api.reports.verwerfen(reportId, begruendung.trim() || null),
    onSuccess: () => {
      invalidieren()
      setVerwerfenOffen(false)
      toast.success("Meldung verworfen")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  if (error != null) {
    return (
      <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">{apiError(error)}</p>
    )
  }
  if (!report) return <p className="text-muted-foreground">Lade Meldung …</p>

  const kontext = report.kontext ?? {}
  const prioAenderbar = istKundenadmin || report.status === "entwurf"

  return (
    <div className="mx-auto flex max-w-[880px] flex-col gap-4">
      <div className="text-[13px]">
        <Link to="/" className="font-semibold text-primary hover:underline">
          ← Zur Übersicht
        </Link>
      </div>

      <div className="flex items-start justify-between gap-5">
        <div>
          <h1 className="mb-2 text-[22px] font-bold">
            {report.titel || <em className="text-muted-foreground">ohne Titel</em>}
          </h1>
          <p className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_FARBE[report.status]}`}
            >
              {STATUS_LABEL[report.status]}
            </span>
            <span>
              {report.typ ? TYP_LABEL[report.typ] : "—"} · #{report.id} · gemeldet von{" "}
              {report.melderName || report.melderSub} am{" "}
              {new Date(report.createdAt).toLocaleString("de-DE")}
            </span>
            {report.forgejoIssueUrl && (
              <a
                href={report.forgejoIssueUrl}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-primary underline"
              >
                Forgejo-Issue #{report.forgejoIssueNr}
              </a>
            )}
          </p>
        </div>
        {istKundenadmin && report.status === "neu" && (
          <button
            type="button"
            onClick={() => setVerwerfenOffen(true)}
            className="whitespace-nowrap rounded-lg border border-destructive/30 bg-card px-4 py-2 text-[13px] font-semibold text-destructive hover:bg-destructive/5"
          >
            Verwerfen
          </button>
        )}
      </div>

      {report.status === "entwurf" && (
        <p className="rounded-xl bg-warning/10 px-4 py-3 text-sm text-warning">
          Entwurf — noch nicht abgesendet.{" "}
          <Link to={`/melden?entwurf=${report.id}`} className="underline">
            Weiter bearbeiten
          </Link>
        </p>
      )}

      {istReviewer && report.status !== "entwurf" && <ReviewAktionen report={report} />}

      {/* F13/S8: Präzisierungs-Assistent — nur Reviewer, vor der Freigabe */}
      {istReviewer && (report.status === "neu" || report.status === "gesichtet") && (
        <PraezisierungsAssistent report={report} />
      )}

      {report.begruendungAblehnung && (
        <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Abgelehnt durch netz-fabrik: {report.begruendungAblehnung}
        </p>
      )}

      {report.begruendungVerwerfung && (
        <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Verworfen durch den Kundenadmin: {report.begruendungVerwerfung}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className={SEKTION}>
          <h2 className={SEKTION_TITEL}>
            {report.typ === "fehler" ? "Ist-Verhalten" : "Heutiger Zustand"}
          </h2>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {report.istBeschreibung || "—"}
          </p>
        </section>
        <section className={SEKTION}>
          <h2 className={SEKTION_TITEL}>
            {report.typ === "fehler" ? "Soll-Verhalten" : "Gewünschtes Verhalten"}
          </h2>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {report.sollBeschreibung || "—"}
          </p>
        </section>
      </div>

      {report.akzeptanzkriterien && report.akzeptanzkriterien.length > 0 && (
        <section className={SEKTION}>
          <h2 className={SEKTION_TITEL}>Akzeptanzkriterien</h2>
          <ul className="list-disc pl-5 text-sm leading-7">
            {report.akzeptanzkriterien.map((k, i) => (
              <li key={i}>{k}</li>
            ))}
          </ul>
        </section>
      )}

      {(report.machbarkeit || report.modulId || report.duplikatVon) && (
        <section className={SEKTION}>
          <h2 className={SEKTION_TITEL}>Einordnung</h2>
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-sm">
            <dt className="text-muted-foreground">Tabelle</dt>
            <dd className="font-medium">
              {report.modulId ?? "—"}
              {/* F32: Reviewer springt in den Struktur-Browser der Tabelle */}
              {istReviewer && report.modulId?.includes(":") && (
                <Link
                  to={`/admin/schema?db=${encodeURIComponent(report.modulId.split(":")[0])}&tabelle=${encodeURIComponent(report.modulId.split(":")[1])}`}
                  className="ml-2 font-semibold text-primary underline"
                >
                  Im Struktur-Browser öffnen
                </Link>
              )}
            </dd>
            <dt className="text-muted-foreground">Machbarkeit (unverbindlich)</dt>
            <dd className="font-medium">{report.machbarkeit ?? "—"}</dd>
            <dt className="text-muted-foreground">Mögliches Duplikat</dt>
            <dd>
              {report.duplikatVon ? (
                <Link to={`/meldungen/${report.duplikatVon}`} className="font-medium text-primary underline">
                  Meldung #{report.duplikatVon}
                </Link>
              ) : (
                <span className="text-muted-foreground/70">—</span>
              )}
            </dd>
          </dl>
        </section>
      )}

      <section className={SEKTION}>
        <h2 className={SEKTION_TITEL}>Priorität (Kundensicht)</h2>
        <div className="inline-flex rounded-[10px] bg-secondary p-[3px]">
          {(["niedrig", "mittel", "hoch"] as const).map((p) => (
            <button
              key={p}
              type="button"
              disabled={!prioAenderbar || prioAendern.isPending}
              onClick={() => p !== report.prioritaet && prioAendern.mutate(p)}
              className={`rounded-lg px-4 py-1.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed ${
                report.prioritaet === p
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground enabled:hover:text-foreground disabled:opacity-70"
              }`}
            >
              {PRIO_LABEL[p]}
            </button>
          ))}
        </div>
      </section>

      {report.screenshots.length > 0 && (
        <section className={SEKTION}>
          <h2 className={SEKTION_TITEL}>Screenshots</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {report.screenshots.map((s) => (
              <ScreenshotBild key={s.id} reportId={report.id} screenshot={s} />
            ))}
          </div>
        </section>
      )}

      {report.forgejoIssueNr != null && (
        <section className={SEKTION}>
          <h2 className={SEKTION_TITEL}>Chat zur Umsetzung</h2>
          <MeldungsChat reportId={report.id} />
        </section>
      )}

      <section className={SEKTION}>
        <h2 className={SEKTION_TITEL}>Ninox-Stelle & Kontext (F8)</h2>
        <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-[13px]">
          <dt className="text-muted-foreground">Tabelle</dt>
          <dd>
            {String(kontext.tabelleName ?? "—")}
            {kontext.tabelleId ? (
              <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                ({String(kontext.tabelleId)})
              </span>
            ) : null}
          </dd>
          <dt className="text-muted-foreground">Ninox-Team</dt>
          <dd className="font-mono">{String(kontext.ninoxTeamId ?? "—")}</dd>
          <dt className="text-muted-foreground">Datenbank</dt>
          <dd className="font-mono">{String(kontext.ninoxDatabaseId ?? "—")}</dd>
          <dt className="text-muted-foreground">Sicht</dt>
          <dd className="font-mono">{String(kontext.viewId ?? "—")}</dd>
          <dt className="text-muted-foreground">Umgebung</dt>
          <dd>
            {kontext.umgebung === "live" ? (
              <span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-bold text-warning">
                Live (echte Daten)
              </span>
            ) : kontext.umgebung === "dev" ? (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">
                DEV (Testdaten)
              </span>
            ) : (
              "—"
            )}
          </dd>
          <dt className="text-muted-foreground">Ninox-Link</dt>
          <dd className="break-all font-mono text-xs">{String(kontext.ninoxUrl ?? "—")}</dd>
          <dt className="text-muted-foreground">Zeitpunkt</dt>
          <dd>
            {kontext.zeitpunkt ? new Date(String(kontext.zeitpunkt)).toLocaleString("de-DE") : "—"}
          </dd>
          <dt className="text-muted-foreground">Browser</dt>
          <dd className="break-all font-mono text-xs">{String(kontext.browser ?? "—")}</dd>
        </dl>
      </section>

      {report.chat.length > 0 && (
        <section className={SEKTION}>
          <h2 className={SEKTION_TITEL}>Erfassungs-Dialog (Protokoll)</h2>
          <div className="flex flex-col gap-2">
            {report.chat.map((n) => (
              <div
                key={n.id}
                className={`max-w-[75%] whitespace-pre-wrap rounded-[10px] px-3 py-2 text-[13px] ${
                  n.rolle === "kunde" ? "self-end bg-primary/10" : "self-start bg-muted"
                }`}
              >
                {n.inhalt}
              </div>
            ))}
          </div>
        </section>
      )}

      {report.verlauf.length > 0 && (
        <section className={SEKTION}>
          <h2 className={SEKTION_TITEL}>Verlauf</h2>
          <div className="flex flex-col">
            {report.verlauf.map((v, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={`mt-1 h-[9px] w-[9px] shrink-0 rounded-full ${
                      i === 0 ? "bg-primary" : "bg-border"
                    }`}
                  />
                  {i < report.verlauf.length - 1 && <span className="w-px flex-1 bg-border" />}
                </div>
                <div className="pb-3.5 text-[13px]">
                  <span className="text-muted-foreground/70">
                    {new Date(v.zeitpunkt).toLocaleString("de-DE")}
                  </span>{" "}
                  <span>
                    {v.vonStatus ? `${STATUS_LABEL[v.vonStatus]} → ` : ""}
                    {STATUS_LABEL[v.nachStatus]}
                  </span>
                  {v.begruendung && (
                    <span className="text-muted-foreground"> ({v.begruendung})</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Verwerfen-Dialog (F21) */}
      {verwerfenOffen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-4">
          <div className="w-full max-w-md rounded-[14px] bg-card p-5 shadow-2xl">
            <h2 className="mb-2 font-bold">Meldung verwerfen</h2>
            <p className="mb-3 text-sm text-muted-foreground">
              Die Meldung geht nicht in die Sichtung bei netz-fabrik. Die Begründung ist für
              den Melder sichtbar.
            </p>
            <textarea
              value={begruendung}
              onChange={(e) => setBegruendung(e.target.value)}
              rows={3}
              placeholder="Begründung (optional)"
              className="mb-3 w-full rounded-lg border px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setVerwerfenOffen(false)}
                className="rounded-lg bg-secondary px-4 py-2 text-[13px] font-semibold"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => verwerfen.mutate()}
                disabled={verwerfen.isPending}
                className="rounded-lg bg-destructive px-4 py-2 text-[13px] font-semibold text-destructive-foreground disabled:opacity-50"
              >
                Verwerfen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
